import type { Build } from '@/api/schemas';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { currentStep, lifecycleSteps, stepProgress, type Step } from '../buildHelpers';
import { connectorClass, labelClass, progressTone, stepColor, stepFilled, stepIcon } from './stepperModel';

/**
 * Lifecycle stepper of screen 03: one node per stage; the connector fills up to the active stage. On a phone it folds
 * into one line ("Step 3 of 6 · Waiting in the build queue") over a progress bar, so nothing scrolls sideways.
 */
export function BuildStepper({ build }: { build: Build }) {
  const steps = lifecycleSteps(build);
  return (
    <Card className="enter p-4 sm:p-5" data-testid="build-stepper">
      <CompactStepper steps={steps} />
      <ol className="flex items-start max-sm:hidden">
        {steps.map((s, i) => (
          <StepItem key={s.key} step={s} index={i} last={i === steps.length - 1} />
        ))}
      </ol>
    </Card>
  );
}

function CompactStepper({ steps }: { steps: Step[] }) {
  const current = currentStep(steps);
  if (!current) return null;
  const { step, position } = current;
  return (
    <div className="flex flex-col gap-2.5 sm:hidden" data-testid="build-stepper-compact" data-state={step.state}>
      <div className="flex min-w-0 items-center gap-2">
        <StepGlyph step={step} size={20} />
        <span className="shrink-0 text-xs text-subtle">
          Step {position} of {steps.length}
        </span>
        <span aria-hidden className="text-subtle">
          ·
        </span>
        <span className={cn('min-w-0 truncate text-sm font-medium', labelClass(step.state))}>{step.label}</span>
      </div>
      <Progress value={stepProgress(steps)} aria-label={`Step ${position} of ${steps.length}: ${step.label}`} indicatorClassName={progressTone(step.state)} />
      {step.detail ? <span className="truncate font-mono text-xs text-subtle">{step.detail}</span> : null}
    </div>
  );
}

/** The step's icon; the active one spins a ring around it (styles/motion.css `.spin`, still under reduced motion). */
function StepGlyph({ step: s, size }: { step: Step; size: number }) {
  return (
    <span className={cn('relative inline-flex shrink-0', stepColor(s.state))}>
      <Icon name={stepIcon(s.state)} size={size} fill={stepFilled(s.state)} className={cn('shrink-0', s.state === 'active' && 'pulse rounded-full')} label={`${s.label}: ${s.state}`} />
      {s.state === 'active' ? <span aria-hidden className="spin absolute -inset-1 rounded-full border-2 border-current border-t-transparent" /> : null}
    </span>
  );
}

function StepItem({ step: s, index, last }: { step: Step; index: number; last: boolean }) {
  return (
    <li data-state={s.state} className="flex min-w-0 flex-1 flex-col gap-2">
      <div className="flex items-center">
        <StepGlyph step={s} size={22} />
        {last ? null : <span aria-hidden className={cn('mx-2 h-0.5 flex-1 rounded-full', connectorClass(s.state))} />}
      </div>
      <div className="min-w-0 pr-3">
        <div className={cn('text-[13px] font-medium leading-snug', labelClass(s.state))} title={s.label}>
          {index + 1}. {s.label}
        </div>
        {s.detail ? <div className="truncate font-mono text-xs text-subtle" title={s.detail}>{s.detail}</div> : null}
      </div>
    </li>
  );
}
